<template>
	<v-container grid-list-xl>
		<slot name="article-actions" />
		<v-form
			ref="form"
			@submit.prevent="submit(false)"
		>
			<v-layout
				row
				wrap
				align-center
			>
				<v-flex
					v-if="edit || extended"
					grow
					py-0
				>
					<v-select
						v-model="custom[dashedCategories.name]"
						:label="dashedCategories.label"
						:items="dashedCategories.options"
						:disabled="!!report && report.isArchivedOrParentArchived"
						item-text="label"
						item-value="value"
						prepend-icon="add"
						@click:prepend="newCategoryShow"
					/>
				</v-flex>
				<v-flex
					:sm12="extended === true"
					:lg9="extended === false && !edit"
					xs12
				>
					<v-form
						ref="urlsForm"
						v-model="valid"
						@submit.prevent="submit(false)"
					>
						<v-text-field
							v-if="edit"
							v-model="editedArticle.url"
							name="url"
							label="URL"
							disabled
						/>
						<v-textarea
							v-else
							v-model="urls"
							:disabled="edit"
							:rules="[validate.urlRules, ...validate.require]"
							:required="true"
							autofocus
							name="url"
							label="Add URLs (1 per line) *"
						/>
					</v-form>
				</v-flex>
				<v-flex
					v-if="edit"
					xs12
					class="py-0"
				>
					<v-layout wrap>
						<v-flex>
							<v-combobox
								ref="publication"
								label="Publication name"
								v-model="editedArticle.publicationName"
								:items="publicationNames"
								item-text="v"
								item-value="v"
								:rules="[...validate.require, validate.max(editedArticle.publicationName, 255)]"
								:loading="publicationNamesLoading"
								:disabled="publicationNamesLoading"
								clearable
								hide-no-data
								:return-object="false"
								@input.native="searchPublicationNames"
							/>
						</v-flex>
						<v-flex
							v-if="editedArticle.hasAlternatives('publicationName')"
							shrink
							px-0
						>
							<AlternativesDropdownMenu
								attr="publicationName"
								:article="editedArticle"
								@update="updateFromAlternatives"
							/>
						</v-flex>
					</v-layout>
					<v-layout wrap align-center>
						<v-flex>
							<v-text-field
								ref="title"
								v-model="editedArticle.title"
								:rules="[...validate.require, validate.max(editedArticle.title, 255)]"
								:required="true"
								name="title"
								label="Title *"
							/>
						</v-flex>
						<v-flex
							v-if="editedArticle.hasAlternatives('title')"
							shrink
							px-0
						>
							<AlternativesDropdownMenu
								attr="title"
								:article="editedArticle"
								@update="updateFromAlternatives"
							/>
						</v-flex>
					</v-layout>
					<v-layout wrap align-center>
						<v-flex>
							<v-combobox
								ref="author"
								v-model="editedArticle.author"
								:rules="[
									...$store.get('isSkilled', 'author_issue') ? validate.require : [],
									validate.max(editedArticle.author, 100),
									validateAuthor(editedArticle.author)
								]"
								:required="true"
								:items="authorNames"
								:label="$store.get('isSkilled', 'author_issue') ? 'Author *' : 'Author'"
								hide-no-data
								@input.native="searchAuthorNames"
							/>
						</v-flex>
						<v-flex
							v-if="editedArticle.hasAlternatives('author')"
							shrink
							px-0
						>
							<AlternativesDropdownMenu
								attr="author"
								:article="editedArticle"
								@update="updateFromAlternatives"
							/>
						</v-flex>
					</v-layout>
					<v-layout wrap align-center>
						<v-flex>
							<v-menu
								v-model="dateMenu"
								:nudge-right="40"
								:close-on-content-click="false"
								lazy
								transition="scale-transition"
								offset-y
								full-width
								max-width="290px"
								min-width="290px"
							>
								<v-text-field
									ref="date"
									slot="activator"
									:rules="[...validate.require, validate.date, validateDate]"
									:value="editedArticle.date"
									label="Date *"
									hint="YYYY-MM-DD"
									persistent-hint
									prepend-icon="event"
									readonly
								/>
								<v-date-picker
									v-model="editedArticle.date"
									no-title
									@input="dateMenu = false"
								/>
							</v-menu>
						</v-flex>
						<v-flex
							v-if="editedArticle.hasAlternatives('date')"
							shrink
							px-0
						>
							<AlternativesDropdownMenu
								attr="date"
								:article="editedArticle"
								@update="updateFromAlternatives"
							/>
						</v-flex>
					</v-layout>
					<slot name="summary" />
					<v-layout>
						<v-flex>
							<v-textarea
								ref="summary"
								v-model="editedArticle.summary"
								:rules="validate.require"
								:required="true"
								name="summary"
								label="Summary *"
							/>
						</v-flex>
						<v-flex
							v-if="editedArticle.hasAlternatives('summary')"
							shrink
							px-0
						>
							<AlternativesDropdownMenu
								attr="summary"
								:article="editedArticle"
								@update="updateFromAlternatives"
							/>
						</v-flex>
					</v-layout>
					<div>
						<v-checkbox
							v-model="enableAlternatePaste"
							label="Enable alternate paste"
							color="primary"
							class="mt-0 mb-0"
							hide-details
						/>
						<v-textarea
							ref="bodyText"
							auto-grow
							v-model="editedArticle.body"
							:rules="validate.require"
							name="body"
							label="Body *"
							@paste="handlePaste"
						/>
					</div>
				</v-flex>
				<template v-if="!edit && extended">
					<v-flex
						xs12
						py-0
						mb-3
						mt-2
					>
						<v-select
							v-if="$store.get('isSkilled', 'importance')"
							v-model="importance"
							label="Set Importance"
							:items="importanceChoices"
						/>
						<v-checkbox
							v-else
							label="Mark as Important"
							v-model="importance"
							:value="1"
							color="primary"
						/>
					</v-flex>
				</template>
				<template v-if="extended || edit">
					<v-flex
						v-for="(field, index) in customFields"
						:key="index"
						:sm12="['textInput', 'textarea'].includes(field.type)"
						xs12
						sm6
						py-0
						v-if="!(field.disabled && [null, false, '', undefined].includes(custom[field.name]))"
					>
						<template v-if="field.hasCompletion && ['textInput', 'textarea'].includes(field.type)">
							<v-text-field
								v-if="field.type === 'textInput'"
								:disabled="field.disabled"
								:ref="field.name"
								:name="field.name"
								:label="field.label"
								v-model="custom[field.name]"
								append-outer-icon="auto_fix_high"
								@click:append-outer="generateCompletion(field)"
							/>
							<v-textarea
								v-if="field.type === 'textarea'"
								:disabled="field.disabled"
								:ref="field.name"
								:name="field.name"
								:label="field.label"
								v-model="custom[field.name]"
								append-outer-icon="auto_fix_high"
								@click:append-outer="generateCompletion(field)"
							/>
						</template>
						<template v-else-if="field.type === 'textInput'">
							<v-text-field
								:disabled="field.disabled"
								:name="field.name"
								:label="field.label"
								v-model="custom[field.name]"
							/>
						</template>
						<template v-else-if="field.type === 'number'">
							<v-text-field
								:disabled="field.disabled"
								:name="field.name"
								:label="field.label"
								:step="field.config.step"
								type="number"
								autocomplete="off"
								hint="Numbers only"
								v-model="custom[field.name]"
							/>
						</template>
						<template v-else-if="field.type === 'textarea'">
							<v-textarea
								:disabled="field.disabled"
								:name="field.name"
								:label="field.label"
								v-model="custom[field.name]"
							/>
						</template>
						<template v-else-if="field.type === 'checkbox'">
							<v-checkbox
								:disabled="field.disabled"
								:label="field.label"
								v-model="custom[field.name]"
								color="primary"
							/>
						</template>
						<template
							v-else-if="field.type === 'dropdownList' && (field.options.length === 2)
								&& field.name !== 'category'"
						>
							<label class="v-label v-label--active caption">
								{{ field.label }}
							</label>
							<v-checkbox
								:disabled="field.disabled"
								:label="field.options[1].label"
								:true-value="field.options[1].value"
								:value="field.options[1].value"
								v-model="custom[field.name]"
								color="primary"
								class="mt-0"
							/>
						</template>
						<template v-else-if="field.type === 'dropdownList'">
							<v-select
								:disabled="field.disabled"
								v-model="custom[field.name]"
								:label="field.label"
								:items="field.options"
								item-text="label"
								item-value="value"
							/>
						</template>
					</v-flex>
				</template>
				<v-flex
					:lg3="!extended && !edit"
					sm12
					pb-3
					class="text-xs-center"
				>
					<v-btn
						:disabled="!valid"
						type="submit"
						ripple
						class="primary"
					>
						Submit
					</v-btn>
					<v-btn
						v-if="singleURL"
						:disabled="!valid"
						class="secondary"
						@click="submit(true)"
					>
						Submit and Edit
					</v-btn>
					<v-btn
						v-if="closable"
						color="error"
						flat
						@click="closeArticleForm()"
					>
						Close
					</v-btn>
				</v-flex>
				<v-flex v-if="!edit" pa-0 style="position: absolute; right: 0; bottom: 0;">
					<v-btn
						small
						flat
						color="secondary"
						@click="extended = !extended"
					>
						<span v-if="extended">Hide Additional Options</span>
						<span v-else>Show Additional Options</span>
					</v-btn>
				</v-flex>
			</v-layout>
		</v-form>
		<Dialog
			:show="completions.show"
			:loading="completions.loading"
			:title="completions.title"
			maxWidth="800"
			@close="closeCompletions()"
		>
			<template slot="content">
				<v-card
					v-for="(alternative, index) in completions.alternatives"
					:key="index"
					:hover="true"
					class="my-3"
					@click="selectCompletion(index)"
				>
					<v-card-text>
						{{ alternative }}
					</v-card-text>
				</v-card>
			</template>
		</Dialog>
		<NewCategoryDialog
			:show="categoryDialog"
			@close="newCategory(false)"
			@close-new="newCategory(true)"
		/>
		<Confirm ref="confirm" />
	</v-container>
</template>

<script>
import { get } from 'lodash';
import Validation from '@/mixins/Validation';
import NewCategoryDialog from '@/components/Report/NewCategoryDialog';
import Confirm from '@/components/Confirm';
import Dialog from '@/components/Global/Dialog';
import AlternativesDropdownMenu from '@/components/Article/AlternativesDropdownMenu';
import axios from '@/util/axios';
import { getLastCategorySelected, setLastCategorySelected } from '@/util/lastCategorySelected';

export default {
	components: {
		NewCategoryDialog,
		Confirm,
		AlternativesDropdownMenu,
		Dialog,
	},
	mixins: [Validation],
	props: {
		edit: { type: Boolean, default: false },
		editArticle: { type: Object, default: null },
		closable: { type: Boolean, default: true },
	},
	data() {
		return {
			valid: true,
			extended: this.$ls.get('showArticleAdditionalOptions', false),
			urls: '',
			custom: {},
			importance: 100,
			// generated array of 200:
			importanceChoices: [...Array(200).keys()].map(i => ({ text: i + 1, value: i + 1 })),
			editedArticle: {},
			dateMenu: false,
			categoryDialog: false,
			showBody: false,
			enableAlternatePaste: false,

			// For publication names search
			publicationNames: [],
			publicationNamesLoading: false,

			authorNames: [],
			
			completions: {
				loading: false,
				show: false,
				title: 'Completions',
				alternatives: [],
				fieldName: '',
			},
		};
	},
	created() {
		this.$store.dispatch('loadAuthorNames');

		if (this.editArticle) {
			this.editedArticle = this.editArticle;

			// Some old articles were allowed to process with invalid dates such as 0001-01-01
			// but dates like that cause the date picker to not work so we must sanitize them
			if (this.validateDate(this.editArticle.date) !== true) {
				this.editArticle.date = null;
			}

			this.custom = this.editArticle.customData || {};
		}

		if (!this.edit) {
			// Set last used category to custom data in form
			this.custom.category = getLastCategorySelected(this.project.id);
		}
	},
	mounted() {
		if (this.editArticle) {
			this.$refs.form.validate();
		}
	},
	computed: {
		project() {
			return this.$store.get('projects/currentProject');
		},
		report() {
			return this.$store.get('reports/currentReport');
		},
		isChanged() {
			if (!this.edit) {
				return true;
			}
			if (this.edit && this.editArticle.changed()) {
				return true;
			}
			return false;
		},
		dashedCategories() {
			const categories = this.$store.copy('categories')
				.map(o => ({ label: o.name, value: o.id, level: o.level }));

			categories.forEach((cat) => {
				let dashAmount = cat.level;
				while (dashAmount > 1) {
					if (dashAmount === cat.level) {
						cat.label = `\u2014 ${cat.label}`;
					}
					else {
						cat.label = `\u2014${cat.label}`;
					}
					dashAmount--;
				}
			});
			// Default no option
			categories.unshift({ label: '', value: 0 });
			return { name: 'category', label: 'Category', type: 'dropdownList', options: categories };
		},
		customFields() {
			const fields = this.project.customFields;
			const customs = [];
			if (!fields) {
				return [];
			}
			fields.forEach((storeField) => {
				// Remove this once we no longer receive categories from store customfields
				if (storeField.name === 'category') {
					return;
				}
				const field = {
					name: storeField.name,
					type: storeField.type,
					label: storeField.label + (storeField.disabled ? ' (disabled)' : ''),
					disabled: storeField.disabled,
					hasCompletion: storeField.hasCompletion,
					options: [],
					value: '',
					config: storeField.config || {},
				};
				if (storeField.type === 'checkbox') {
					field.value = false;
				}
				else if (storeField.type === 'textInput') {
					if (['integer', 'decimal'].includes(field.config.type)) {
						field.type = 'number';

						field.config.decimals = field.config.decimals ?? 0;

						field.config.step = field.config.step ?? (1 / (10 ** field.config.decimals));
					}
				}
				
				if (storeField.items) {
					// Add default no value option
					field.options.push({
						label: '',
						value: null,
						disabled: false,
					});
					// eslint-disable-next-line
					for (const property in storeField.items.slice()) {
						// https://stackoverflow.com/a/49028665/1448147
						const item = Object.assign({}, storeField.items[property]);

						// skip if disabled and not currently selected
						if (item.disabled && item.value !== this.editedArticle.customData[field.name]) {
							continue;
						}

						field.options.push({
							label: item.label + (item.disabled ? ' (disabled)' : ''),
							value: item.value,
							disabled: item.disabled,
						});
					}
					console.log(field.options);
				}
				customs.push(field);
			});
			return customs;
		},
		singleURL() {
			if (!this.urls) {
				return false;
			}
			const urls = this.urls.split('\n').filter(Boolean);
			if (urls.length === 1) {
				return true;
			}
			return false;
		},
	},
	methods: {
		updateFromAlternatives(attr, value) {
			if (this.editedArticle[attr] === undefined) 
				return;

			this.editedArticle[attr] = value;
		},
		searchPublicationNames(e) {
			// Read the searched text
			const text = e.srcElement.value;
			
			// update directly according to https://github.com/vuetifyjs/vuetify/issues/4679#issuecomment-469009775 
			this.editedArticle.publicationName = text;
			
			// Clear items, so v-autocomplete won't search new value from previous results
			this.publicationNames = [];

			// Update model publication name while typing in case user clicks on submit without unfocusing this field
			// But only for publication name combobox
			this.editedArticle.publicationName = text;

			// Clear previous timeout
			if (this.timeout) {
				clearTimeout(this.timeout);
			}

			this.timeout = setTimeout(() => {
				if (text.length < 3) {
					return;
				}
				this.publicationNamesLoading = true;
				axios.get(`/publications/all-names?q=${text}`)
					.then(r => {
						this.publicationNames = r.data;
					})
					.catch((e) => {
						this.publicationNames = [];
						dump(e);
					})
					.finally(() => {
						this.publicationNamesLoading = false;
					});
			}, 1000);
		},
		searchAuthorNames(e) {
			// update directly according to https://github.com/vuetifyjs/vuetify/issues/4679#issuecomment-469009775 
			this.editedArticle.author = e.srcElement.value;
			
			this.authorNames = e.srcElement.value.length > 2 ? this.$store.get('authorNames') : [];
		},
		submit(isSingle) {
			// If no changes just close the edit form
			if (!this.isChanged) {
				this.closeArticleForm();
				return;
			}

			const categoryId = this.custom.category || null;
			// Set selected category as last used to local storage
			setLastCategorySelected(this.project.id, categoryId);

			if (this.validateURLS() && !this.edit) {
				// remove false items (whitespace) from url list
				const urls = this.urls.split(' ').join('\n').split('\n').filter(Boolean);
				this.$emit('save-article', this.custom, urls, isSingle, this.importance);
				this.custom = {};
				this.$refs.urlsForm.reset();
				this.$refs.form.reset();
			}
			else if (this.edit) {
				const requiredRefs = ['publication', 'title', 'author', 'date', 'bodyText', 'summary'];
				const errors = [];
				requiredRefs.forEach((ref) => {
					if (this.$refs[ref].errorBucket.length !== 0) {
						errors.push(`${ref.charAt(0).toUpperCase()}${ref.slice(1)}: ${this.$refs[ref].errorBucket[0]}`);
					}
				});
				if (!this.$refs.form.validate()) {
					this.$refs.confirm
						.createConfirm('This article has errors, save anyways?', 'error', `${errors.join(', ')}.`)
						.then((answer) => {
							if (!answer) {
								return;
							}
							this.emitEditArticle();
						});
					return;
				}
				this.emitEditArticle();
			}
		},
		emitEditArticle() {
			// v-combobox doesn't update until it blurs, so put emit last in the queue
			// https://github.com/vuetifyjs/vuetify/issues/4679
			setTimeout(() => {
				this.$emit('edit-article', this.custom, this.editedArticle);
			});
		},
		closeArticleForm() {
			this.$emit('close');
		},
		newCategoryShow() {
			this.categoryDialog = true;
		},
		newCategory(newAdded) {
			// Dashed option 0 will always be an empty category 1 will be the new category.
			if (newAdded && this.dashedCategories.options.length > 1) {
				this.custom.category = this.dashedCategories.options[1].value;
			}
			this.categoryDialog = false;
		},
		_get(object, path, defaultValue) {
			return get(object, path, defaultValue);
		},
		closeCompletions() {
			this.completions = {
				loading: false,
				show: false,
				title: 'Suggestions',
				alternatives: [],
				fieldName: '',
			};
		},
		selectCompletion(index) {
			this.custom[this.completions.fieldName] = this.completions.alternatives[index];
			this.closeCompletions();
		},
		generateCompletion(field) {
			this.completions.fieldName = field.name;
			this.completions.title = `Suggestions for ${field.label}`;
			this.completions.loading = true;
			this.completions.show = true;
			// const rand = Math.random().toString(36).substring(7);
			// this.custom[field.name] = rand;
			// console.log(this.custom[field.name]);
			
			this.editedArticle.generateCompletions(field.name).then(() => {
				this.completions.alternatives = this.editedArticle.completions;
				//this.custom[field.name] = this.editedArticle.completions[0];
				this.completions.loading = false;
				//console.log(this.editedArticle.completions);
			});
		},
		cleanHtml(str) {
			// Parse the HTML
			let parser = new DOMParser();
			let dom = parser.parseFromString(str, 'text/html');
			
			// Get the HTML content first (not textContent)
			let html = dom.body.innerHTML;
			
			// Replace tags that normally force a newline with actual newlines
			html = html.replace(/<\/?(?:br|div|p|h\d|blockquote|table|tr|thead|tbody|th|td|caption|pre|address)\b[^>]*>/gi, '\n');
			
			// Replace non-breaking spaces with regular spaces
			html = html.replace(/&nbsp;/gi, ' ');
			
			// Replace list item tags with formatted markers
			html = html.replace(/<li\b[^>]*>/gi, '\n--');
			html = html.replace(/<dt\b[^>]*>/gi, '\n');
			html = html.replace(/<dd\b[^>]*>/gi, '\n  ');
			
			// Protect single < that aren't part of tags
			html = html.replace(/<([^/a-zA-Z])/g, '&lt;$1');
			
			// Strip remaining tags
			html = html.replace(/<[^>]+>/g, '');
			
			// Restore protected < characters
			html = html.replace(/&lt;/g, '<');
			
			// Decode HTML entities
			let tempDiv = document.createElement('div');
			tempDiv.innerHTML = html;
			let text = tempDiv.textContent;
			
			// Preserve existing newlines from the original text
			text = text.replace(/\r\n/g, '\n'); // Normalize line endings
			
			// Eliminate excessive whitespace
			text = text.replace(/ {3,}/g, ' ');
			
			// Replace lines with only whitespace with empty lines
			text = text.replace(/^\s+$/gm, '');
			
			// Eliminate extra newlines but preserve at least one
			text = text.replace(/\n{3,}/g, '\n\n');
			
			// Trim
			text = text.trim();
			
			return text;
		},
		setBodyContent(content) {
			this.editedArticle.body = content;
			// Trigger textarea auto-grow after content update
			this.$nextTick(() => {
				this.$refs.bodyText.calculateInputHeight();
			});
		},
		handlePaste(event) {
			// Only process if alternate paste is enabled
			if (!this.enableAlternatePaste) {
				return; // Let default paste behavior happen
			}
			
			// Prevent the default paste behavior
			event.preventDefault();
			
			// Get the clipboard data
			const clipboard = event.clipboardData;
			
			// Try to get HTML content
			let html = clipboard.getData('text/html');
			let plainText = clipboard.getData('text/plain');
			
			// Check if body textarea has content (ignoring whitespace)
			const currentBody = this.editedArticle.body || '';
			
			const processPaste = () => {
				if (html) {
					this.setBodyContent(this.cleanHtml(html));
				}
				else {
					// No HTML content, ask if they want to paste plain text
					this.$refs.confirm
						.createConfirm('No HTML content found in clipboard. Paste plain text instead?', 'info')
						.then((answer) => {
							if (answer) {
								this.setBodyContent(plainText);
							}
						});
				}
			};
			
			if (currentBody.trim()) {
				// Show confirmation dialog if body is not empty
				this.$refs.confirm
					.createConfirm('The body field has content. Do you want to replace it?', 'warning')
					.then((answer) => {
						if (answer) {
							processPaste();
						}
					});
			}
			else {
				// Body is empty, proceed with paste
				processPaste();
			}
		},
	},
	destroyed() {
		clearInterval(this.timeout);
	},
};
</script>
